import { Component } from '@angular/core';
import { FormBuilder, Validators, FormGroup, NgForm, FormControl, FormGroupDirective, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { CognitoService } from 'app/core/http/services/cognito.service';
import { AuthService } from 'app/core/http/services/auth.service';
import { ErrorStateMatcher } from '@angular/material/core';
import { User } from 'app/core/interfaces/user';
import { fetchAuthSession } from 'aws-amplify/auth';
import { OrganizationSubUsersService } from 'app/core/http/services/organizationSubUsers.service';
import { MatSnackBar } from '@angular/material/snack-bar';

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  selector: 'page-login',
  templateUrl: 'login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent {
  public user: any = {} as User;
  public form!: FormGroup;
  public loading = false;
  public isModalOpen = false;
  public successLogin: Boolean = false;
  public submitted: Boolean = false;
  public hide = true;
  public organizationSubUser: any;
  matcher = new MyErrorStateMatcher();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private cognitoService: CognitoService,
    private auth: AuthService,
    private organizationSubUsersService: OrganizationSubUsersService,
    private snackBar: MatSnackBar
  ) {
  }

  ngOnInit() {
    this.form = this.fb.group({
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required, this.validatePassword]),
    });

    console.log("entrei login")
  }

  private getFriendlyErrorMessage(error: any): string {
    if (!error) return 'Ocorreu um erro desconhecido durante o login.';


    if (error.name) {
      switch (error.name) {
        case 'UserUnAuthenticatedException': 
          return 'Sua conta ainda não foi confirmada. Por favor, verifique seu e-mail para confirmar sua conta.';
        case 'NotAuthorizedException':
          return 'E-mail ou senha incorretos. Por favor, tente novamente.';
        case 'UserNotFoundException':
          return 'Nenhuma conta encontrada com este e-mail. Por favor, verifique ou crie uma nova conta.';
        case 'TooManyFailedAttemptsException':
          return 'Muitas tentativas de login falhas. Sua conta foi temporariamente bloqueada por segurança.';
        case 'TooManyRequestsException':
          return 'Muitas solicitações em um curto período. Por favor, aguarde um momento e tente novamente.';
      }
    }

    if (error.message && error.message.includes('Network Error')) {
      return 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.';
    }

    return error.message || 'Ocorreu um erro durante o login. Por favor, tente novamente.';
  }

  openSnackBar(message: string, status: 'success' | 'error' | 'warning') {
    this.snackBar.open(message, '', {
      duration: 5000,
      panelClass: status === 'success' ? 'snack-success' : status === 'error' ? 'snack-error' : 'snack-warning',
      horizontalPosition: 'right',
      verticalPosition: 'top',
    });
  }

  validatePassword(control: AbstractControl): { [key: string]: boolean } | null {
    const password = control.value;
    const hasNumber = /[0-9]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasSpecial = /[^\w\s]/.test(password);

    if (!hasNumber || !hasUpper || !hasLower || !hasSpecial) {
      return { invalidPassword: true };
    }

    return null;
  }

  newRegistration() {
    this.router.navigateByUrl('criar-conta');
  }

  recuperarSenha() {
    this.router.navigateByUrl('recuperar-senha');
  }

  redirectTo(uri: string) {
    this.router.navigateByUrl(uri).then(() => window.location.reload());
  }

  async login() {
    if (this.form.invalid) {
      this.openSnackBar('Por favor, preencha todos os campos corretamente.', 'error');
      return;
    }

    this.loading = true;
    this.submitted = false;
    this.successLogin = false;

    localStorage.clear();

    this.user = {
      email: this.form.value.email,
      password: this.form.value.password,
    };

    try {
      await this.cognitoService.signIn(this.user);

      const userAttributes = await this.cognitoService.getUserAttributes(this.user.email);
      const { standardAttributes, customAttributes } = userAttributes;
      let userSub: string = standardAttributes['sub'];
      let userType = customAttributes['type'];
      let firstAccess = customAttributes['firstAccess'];
      localStorage.setItem('isFirstAccess', firstAccess);
    
      const { accessToken, idToken } = (await fetchAuthSession()).tokens ?? {};
      const userAccessToken = accessToken?.toString();
      const userTokenId: any = idToken?.toString();
      localStorage.setItem('userTokenId', userTokenId);

      localStorage.setItem('organizationSubUser', userType === 'subOrganization' ? 'true' : 'false');
      userType = userType === 'subOrganization' ? 'organization' : userType;

      if (firstAccess == 'true' && localStorage.getItem('organizationSubUser') == 'false') {
        this.auth.setUser([], userType, userAccessToken, userSub);
        this.redirectTo('home');
        return;
      }

      if (localStorage.getItem('organizationSubUser') == 'false') {
        this.cognitoService.getUser(userType, userSub).subscribe({
          next: (res: any[]) => {
            this.auth.setUser(res[0], userType, userAccessToken, userSub);
            this.submitted = true;
            this.successLogin = true;
            this.redirectTo('home');
          },
          error: (err) => {
            console.error(err);
            this.openSnackBar(this.getFriendlyErrorMessage(err), 'error');
            this.loading = false;
          },
        });
      }

      if (localStorage.getItem('organizationSubUser') == 'true') {
        this.organizationSubUsersService.getSubUserBySub(userSub).subscribe({
          next: (organizationSubUser: any) => {
            this.organizationSubUser = organizationSubUser[0];
            localStorage.setItem('subUser', JSON.stringify(this.organizationSubUser));

            this.cognitoService.getUser(userType, this.organizationSubUser.organizationReferenceSub).subscribe({
              next: (res: any[]) => {
                this.auth.setUser(res[0], userType, userAccessToken, userSub);
                this.submitted = true;
                this.successLogin = true;
                this.redirectTo('home');
              },
              error: (err) => {
                console.error(err);
                this.openSnackBar(this.getFriendlyErrorMessage(err), 'error');
                this.loading = false;
              },
            });
          },
          error: (err) => {
            console.error(err);
            this.openSnackBar(this.getFriendlyErrorMessage(err), 'error');
            this.loading = false;
          },
        });
      }
    } catch (error) {
      console.error(error);
      this.openSnackBar(this.getFriendlyErrorMessage(error), 'error');
      this.submitted = true;
      this.successLogin = false;
      this.loading = false;
    }
  }

  async loginWithGoogle() {
    this.loading = true;
    try {
      await this.cognitoService.googleLogin();
      this.openSnackBar('Login com Google realizado com sucesso!', 'success');
    } catch (error) {
      console.error(error);
      this.openSnackBar(this.getFriendlyErrorMessage(error), 'error');
      this.submitted = true;
      this.successLogin = false;
    } finally {
      this.loading = false;
    }
  }

  loginWithFacebook() {
    this.openSnackBar('Login com o Facebook ainda não está disponível. Utilize as outras formas de login!', 'warning');
  }

  loginWithApple() {
    this.openSnackBar('Login com a Apple ainda não está disponível. Utilize as outras formas de login!', 'warning');
  }
}
